"use client"

import React, { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table"
import { Skeleton } from "@/components/ui/skeleton"
import { Checkbox } from "@/components/ui/checkbox"
import { ChevronUp, ChevronDown, ChevronsUpDown, GripVertical, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { ColumnResizer } from "@/components/ui/column-resizer"
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"


export interface SimpleTableColumn<T> {
  key: string
  header: string
  width?: string
  /**
   * 固定在表格右侧：表格比屏幕宽、横向滚动时，这一列一直贴在可见区域右边（行尾操作列用）。
   * 底色不透明，悬停时跟着整行变色（TableRow 是 `group hover:bg-accent`）。
   */
  stickyRight?: boolean
  /**
   * 固定在表格左侧（行首的主键列，如 Inspection Results 入住 Tab 的「入住」）：横向滚动时一直可见。
   * 同 stickyRight：底色不透明，悬停跟着整行变色。
   */
  stickyLeft?: boolean
  /** 表头的自定义内容（如分组列下的小标签）；不给就显示 header 文字。header 仍用于可读名和列配置。 */
  headerContent?: React.ReactNode
  /** 表头和单元格同一对齐（数字列用 right）；不给 = 左对齐（现状）。 */
  align?: 'left' | 'right' | 'center'
  render: (item: T) => React.ReactNode
  sortable?: boolean
  sorted?: 'asc' | 'desc' | undefined
  onSort?: () => void
}

/** 列对齐（表头和单元格一起）：放在默认 text-left 之后，cn 按后者为准 */
const ALIGN_CLASS = { left: 'text-left', right: 'text-right', center: 'text-center' } as const
/** 句首大写的表头：盖掉默认的 uppercase / tracking-wider / 11px（DESIGN.md §2：font-medium + muted） */
const HEAD_SENTENCE_CASE = 'normal-case tracking-normal text-xs'

/** stickyRight 列的样式：放在类名最后，盖掉表头的 `relative`（cn 按后者为准） */
const STICKY_RIGHT_HEAD = 'sticky right-0 z-[11] border-l border-border'
const STICKY_RIGHT_CELL = 'sticky right-0 z-[1] border-l border-border bg-background group-hover:bg-accent'
const STICKY_LEFT_HEAD = 'sticky left-0 z-[11] border-r border-border'
const STICKY_LEFT_CELL = 'sticky left-0 z-[1] border-r border-border bg-background group-hover:bg-accent'

export interface SimpleTableProps<T> {
  data: T[]
  columns: SimpleTableColumn<T>[]
  loading?: boolean
  selectable?: boolean
  selectedItems?: T[]
  onRowClick?: (item: T) => void
  onSelectionChange?: (selectedItems: T[]) => void
  keyExtractor?: (item: T) => string
  emptyMessage?: string
  emptyDescription?: string
  emptyAction?: React.ReactNode
  className?: string
  rowClassName?: string | ((item: T, index: number) => string)
  headerClassName?: string
  /**
   * 表头句首大写（DESIGN.md §2：不要全大写 + 拉开字距）。opt-in：其他列表页的表头不动，逐页迁。
   */
  sentenceCaseHeader?: boolean
  bodyClassName?: string
  // 无限滚动相关属性
  hasMore?: boolean
  onLoadMore?: () => void
  loadingMore?: boolean
  // 容器高度
  containerHeight?: string
  // 数据源标识，用于状态存储
  source?: string
  // 底部内容
  bottomContent?: React.ReactNode
  // 控制哪些行可以被选中
  isRowSelectable?: (item: T) => boolean
  // 隐藏表头全选框，仅保留行级选择
  hideSelectAll?: boolean
  /**
   * Sprint 4 — called when the user releases the mouse after dragging a
   * column's resize handle. Width is in pixels. When omitted, the handle
   * is hidden (backward compatible with pages that haven't opted in).
   */
  onColumnWidthChange?: (key: string, width: number) => void
  /**
   * Sprint 4 — called after the user drags a column to a new position.
   * Receives the full new key order. When omitted, drag handles are hidden.
   */
  onColumnOrderChange?: (newOrder: string[]) => void
}

/**
 * Sortable table head cell — wraps the regular TableHead in a dnd-kit
 * sortable item and prepends a small GripVertical drag handle.
 *
 * The handle is the ONLY draggable surface (via {...attributes, ...listeners}
 * on the icon span). The rest of the header — sort click area, resize handle —
 * stays unaffected.
 */
function SortableHeadCell({
  id,
  className,
  style,
  children,
  'aria-sort': ariaSort,
}: {
  id: string
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
  'aria-sort'?: React.AriaAttributes['aria-sort']
}) {
  const { t } = useTranslation('common')
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id })
  const composedStyle: React.CSSProperties = {
    ...style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : undefined,
  }
  return (
    <TableHead ref={setNodeRef as any} className={className} style={composedStyle} aria-sort={ariaSort}>
      <span
        {...attributes}
        {...listeners}
        aria-label={t('ui.table.dragColumn')}
        className="absolute left-1 top-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </span>
      {children}
    </TableHead>
  )
}

export function SimpleTable<T>({
  data,
  columns,
  loading = false,
  selectable = false,
  selectedItems: externalSelectedItems,
  onRowClick,
  onSelectionChange,
  keyExtractor = (item: any) => item.id,
  emptyMessage,
  emptyDescription,
  emptyAction,
  className,
  rowClassName,
  headerClassName,
  sentenceCaseHeader,
  bodyClassName,
  // 无限滚动相关属性
  hasMore = false,
  onLoadMore,
  loadingMore = false,
  containerHeight = "calc(100vh - 160px)",
  // 数据源标识
  source: _source,
  // 底部内容
  bottomContent,
  isRowSelectable,
  hideSelectAll = false,
  onColumnWidthChange,
  onColumnOrderChange,
}: SimpleTableProps<T>) {
  const { t } = useTranslation('common')
  const resolvedEmptyMessage = emptyMessage ?? t('ui.table.noDataFound')
  const resolvedEmptyDescription = emptyDescription ?? t('ui.table.noItemsFound')
  const [selectedItems, setSelectedItems] = useState<T[]>(externalSelectedItems || [])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLTableSectionElement>(null)
  const lastSelectedIndexRef = useRef<number | null>(null)
  // Sprint 4 — in-progress width during drag, before the parent commits via
  // onColumnWidthChange. Keyed by column.key. Cleared on commit.
  const [dragWidths, setDragWidths] = useState<Record<string, number>>({})

  // Sprint 4 — column reorder via dnd-kit. Sensors require distance > 5px
  // to start a drag, otherwise a sort-click on the header would also
  // trigger a drag.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )
  const handleColumnDragEnd = (event: DragEndEvent) => {
    if (!onColumnOrderChange) return
    const { active, over } = event
    if (!over || active.id === over.id) return
    const order = columns.map((c) => c.key)
    const oldIndex = order.indexOf(active.id as string)
    const newIndex = order.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) return
    const moved = order.slice()
    const [taken] = moved.splice(oldIndex, 1)
    moved.splice(newIndex, 0, taken)
    onColumnOrderChange(moved)
  }

  // 计算全选状态：true = 全选, false = 全不选, "indeterminate" = 部分选中
  const getSelectAllState = (): boolean | "indeterminate" => {
    if (data.length === 0) return false
    const selectedCount = selectedItems.length
    if (selectedCount === 0) return false
    if (selectedCount === data.length) return true
    return "indeterminate"
  }

  const selectAllState = getSelectAllState()

  // 同步外部传入的选中状态
  useEffect(() => {
    if (externalSelectedItems !== undefined) {
      setSelectedItems(externalSelectedItems)
    }
  }, [externalSelectedItems])

  // 排序/筛选变化后滚动回顶部：当 loading 从 true→false 且有数据时重置滚动位置
  const prevLoadingRef = useRef(loading)
  useEffect(() => {
    if (prevLoadingRef.current && !loading && data.length > 0 && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
    prevLoadingRef.current = loading
  }, [loading, data.length])

  // 表头是 `sticky top-0` 且**不透明**（bg-muted）：滚动后它会压在数据行上，压住的那部分
  // 在浏览器眼里仍然「在滚动口内」。于是 scroll-into-view（键盘 Tab 聚焦行内勾选框、
  // `element.scrollIntoView()`、自动化的 `scrollIntoViewIfNeeded`）不会把行滚出来 ——
  // 焦点落在看不见的行上，落点上画着的却是表头的「全选」框。
  // 把表头实际高度设成滚动容器的 scroll-padding-top，滚动口就收缩到表头下沿。
  useEffect(() => {
    const container = scrollContainerRef.current
    const header = headerRef.current
    if (!container || !header) return
    const apply = () => {
      container.style.scrollPaddingTop = `${header.offsetHeight}px`
    }
    apply()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(apply)
    ro.observe(header)
    return () => ro.disconnect()
  }, [loading, data.length])

  // 使用 ref 跟踪 loadingMore 和 hasMore，避免 useEffect 因状态变化重新挂载监听器
  const loadingMoreRef = useRef(loadingMore);
  const hasMoreRef = useRef(hasMore);
  loadingMoreRef.current = loadingMore;
  hasMoreRef.current = hasMore;

  // 滚动到底部加载更多功能
  useEffect(() => {
    // 只在非loading状态且有数据时才设置监听器
    if (loading || data.length === 0 || !onLoadMore) {
      return;
    }

    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const handleScrollToLoad = () => {
      // 通过 ref 读取最新状态，避免闭包陷阱
      if (loadingMoreRef.current || !hasMoreRef.current) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      // 当距离底部小于50px时，触发加载更多
      if (distanceFromBottom < 50) {
        onLoadMore();
      }
    };

    // 添加滚动事件监听器
    container.addEventListener('scroll', handleScrollToLoad, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScrollToLoad);
    };
  }, [loading, data.length, onLoadMore])



  // 处理行点击
  //
  // 「点勾选格 = 选中」与「点行其它区域 = 打开详情」是两个**互斥**的意图。
  // 光靠勾选格里那句 `stopPropagation()` 挡不住第二条：按 DOM 规范，`click`
  // 派发在 mousedown 与 mouseup 两个目标的**最近共同祖先**上 —— 一旦按下和
  // 抬起分属两个元素（勾选格 → 同行的数据格），click 的 target 就直接是
  // `<tr>`，压根不经过勾选格，那句 stopPropagation 没有机会执行，详情抽屉
  // 照样弹出来盖住批量操作条。
  //
  // 真实触发不需要用户刻意拖动：勾中第一行会让批量操作条（约 66px）插进
  // 表格上方，整张表往下位移；紧接着的那一次点击，只要这次重排夹在按下与
  // 抬起之间，落点就分属两个元素了。
  //
  // 所以判据改成**这次点击是不是从勾选格里发起的**：记下按下时的落点，
  // 再叠加一次 target 检查（覆盖不经过 mousedown 的合成点击）。
  const selectionOriginRef = useRef(false)
  const isFromSelectionCell = (e: React.MouseEvent) =>
    !!(e.target as HTMLElement | null)?.closest?.('[data-selection-cell]')

  const handleRowMouseDownCapture = (e: React.MouseEvent) => {
    selectionOriginRef.current = isFromSelectionCell(e)
  }

  const handleRowClick = (item: T, e: React.MouseEvent) => {
    const fromSelection = selectionOriginRef.current || isFromSelectionCell(e)
    selectionOriginRef.current = false
    if (fromSelection) return
    onRowClick?.(item)
  }

  const handleSelectionChange = (item: T, checked: boolean, shiftKey?: boolean) => {
    if (isRowSelectable && !isRowSelectable(item)) return
    const currentIndex = data.indexOf(item)

    // Shift+Click 范围选择
    if (shiftKey && checked && lastSelectedIndexRef.current !== null) {
      const start = Math.min(lastSelectedIndexRef.current, currentIndex)
      const end = Math.max(lastSelectedIndexRef.current, currentIndex)
      const rangeItems = data.slice(start, end + 1).filter(i => !isRowSelectable || isRowSelectable(i))
      const selectedKeys = new Set(selectedItems.map(keyExtractor))
      const newItems = rangeItems.filter(i => !selectedKeys.has(keyExtractor(i)))
      const newSelection = [...selectedItems, ...newItems]
      setSelectedItems(newSelection)
      onSelectionChange?.(newSelection)
      lastSelectedIndexRef.current = currentIndex
      return
    }

    let newSelection: T[]
    if (checked) {
      newSelection = [...selectedItems, item]
    } else {
      newSelection = selectedItems.filter((selectedItem) =>
        keyExtractor(selectedItem) !== keyExtractor(item)
      )
    }
    setSelectedItems(newSelection)
    onSelectionChange?.(newSelection)
    lastSelectedIndexRef.current = currentIndex
  }

  const handleSelectAll = (checked: boolean | "indeterminate") => {
    if (checked === true || checked === "indeterminate") {
      const selectableData = isRowSelectable ? data.filter(isRowSelectable) : data
      setSelectedItems([...selectableData])
      onSelectionChange?.([...selectableData])
    } else {
      setSelectedItems([])
      onSelectionChange?.([])
    }
  }

  const isSelected = (item: T) => {
    return selectedItems.some(selectedItem => 
      keyExtractor(selectedItem) === keyExtractor(item)
    )
  }

  // 渲染加载状态的骨架屏
  if (loading) {
    return (
      <div className="relative">
        {/* Loading 指示器 */}
        <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
          <div className="flex items-center space-x-3 bg-background/90 backdrop-blur-sm px-6 py-4 rounded-xl shadow-lg border">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-base font-medium text-foreground">{t('ui.table.loading')}</span>
          </div>
        </div>

        <Table className={className}>
          <TableHeader className={headerClassName}>
            <TableRow>
              {selectable && (
                <TableHead className="w-12">
                  <Skeleton className="h-4 w-4" />
                </TableHead>
              )}
              {columns.map((column) => (
                <TableHead key={column.key} className={column.width}>
                  <Skeleton className="h-4 w-24" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody className={bodyClassName}>
            {Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={index}>
                {selectable && (
                  <TableCell>
                    <Skeleton className="h-4 w-4" />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell key={column.key}>
                    {column.key === 'avatar' ? (
                      <Skeleton className="h-10 w-10 rounded-full" />
                    ) : (
                      <Skeleton className="h-4 w-full" />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  // 渲染空状态
  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
          <ChevronsUpDown className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-[13px] font-medium text-muted-foreground mb-1">{resolvedEmptyMessage}</p>
        <p className="text-xs text-muted-foreground/70">{resolvedEmptyDescription}</p>
        {emptyAction && <div className="mt-3">{emptyAction}</div>}
      </div>
    )
  }

  // 删除本地计算，由 TablePagination 组件处理

  // 渲染表格
  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* 统一滚动容器 - 同时包含表头和表体 */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto overflow-x-auto"
        style={containerHeight === "auto" ? {} : { height: containerHeight }}
      >
        <div className="relative w-full">
          <table className="min-w-full divide-y divide-border">
          {/* 固定表头 - 使用 sticky 定位 */}
          <TableHeader ref={headerRef} className={cn("bg-muted sticky top-0 z-10", headerClassName)}>
            <TableRow>
              {selectable && (
                <TableHead className="w-10 whitespace-nowrap bg-muted px-3 py-2" style={{ width: '40px' }}>
                  {!hideSelectAll && (
                    <Checkbox
                      checked={selectAllState}
                      onCheckedChange={handleSelectAll}
                      aria-label={t('ui.table.selectAll')}
                    />
                  )}
                </TableHead>
              )}
              {(() => {
                const renderedCells = columns.map((column) => {
                  const dragPx = dragWidths[column.key]
                  const effectiveWidth = dragPx !== undefined ? `${dragPx}px` : column.width
                  const numericInitial =
                    dragPx ??
                    (typeof column.width === 'string' && column.width.endsWith('px')
                      ? parseInt(column.width, 10)
                      : 160)

                  const cellContent = (
                    <>
                      {column.sortable ? (
                        <div
                          // 键盘也能换排序（Enter / 空格），读屏知道它是按钮
                          role="button"
                          tabIndex={0}
                          className={cn("flex items-center space-x-1 cursor-pointer hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm", column.align === 'right' && 'justify-end', column.align === 'center' && 'justify-center')}
                          onClick={column.onSort}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              column.onSort?.()
                            }
                          }}
                        >
                          <span>{column.headerContent ?? column.header}</span>
                          <div className="flex flex-col">
                            {column.sorted === 'asc' ? (
                              <ChevronUp className="h-3 w-3 text-primary" />
                            ) : column.sorted === 'desc' ? (
                              <ChevronDown className="h-3 w-3 text-primary" />
                            ) : (
                              <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                      ) : (
                        column.headerContent ?? column.header
                      )}
                      {onColumnWidthChange && (
                        <ColumnResizer
                          initialWidth={numericInitial}
                          onResize={(w) =>
                            setDragWidths((prev) => ({ ...prev, [column.key]: w }))
                          }
                          onResizeEnd={(finalWidth) => {
                            setDragWidths((prev) => {
                              const { [column.key]: _omit, ...rest } = prev
                              return rest
                            })
                            onColumnWidthChange(column.key, finalWidth)
                          }}
                        />
                      )}
                    </>
                  )

                  return onColumnOrderChange ? (
                    <SortableHeadCell
                      key={column.key}
                      aria-sort={column.sortable ? (column.sorted === 'asc' ? 'ascending' : column.sorted === 'desc' ? 'descending' : 'none') : undefined}
                      id={column.key}
                      className={cn(
                        column.width,
                        "relative whitespace-nowrap text-left bg-muted px-3 py-2 pl-7 text-[11px] font-medium text-muted-foreground uppercase tracking-wider overflow-hidden",
                        sentenceCaseHeader && HEAD_SENTENCE_CASE,
                        ALIGN_CLASS[column.align ?? 'left'],
                        column.stickyRight && STICKY_RIGHT_HEAD,
                        column.stickyLeft && STICKY_LEFT_HEAD,
                      )}
                      style={
                        effectiveWidth
                          ? { width: effectiveWidth, maxWidth: effectiveWidth }
                          : undefined
                      }
                    >
                      {cellContent}
                    </SortableHeadCell>
                  ) : (
                    <TableHead
                      key={column.key}
                      aria-sort={column.sortable ? (column.sorted === 'asc' ? 'ascending' : column.sorted === 'desc' ? 'descending' : 'none') : undefined}
                      className={cn(
                        column.width,
                        "relative whitespace-nowrap text-left bg-muted px-3 py-2 text-[11px] font-medium text-muted-foreground uppercase tracking-wider overflow-hidden",
                        sentenceCaseHeader && HEAD_SENTENCE_CASE,
                        ALIGN_CLASS[column.align ?? 'left'],
                        column.stickyRight && STICKY_RIGHT_HEAD,
                        column.stickyLeft && STICKY_LEFT_HEAD,
                      )}
                      style={
                        effectiveWidth
                          ? { width: effectiveWidth, maxWidth: effectiveWidth }
                          : {}
                      }
                    >
                      {cellContent}
                    </TableHead>
                  )
                })

                if (onColumnOrderChange) {
                  return (
                    <DndContext
                      sensors={dndSensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleColumnDragEnd}
                      // Portal dnd-kit's a11y live-region / described-by nodes to
                      // <body>. By default they render inline inside this <tr>,
                      // producing invalid HTML (a <div> child of <tr>) → React
                      // hydration error on every page using this table. Portaling
                      // moves them out of the table without changing the DnD.
                      accessibility={{
                        container: typeof document !== 'undefined' ? document.body : undefined,
                      }}
                    >
                      <SortableContext
                        items={columns.map((c) => c.key)}
                        strategy={horizontalListSortingStrategy}
                      >
                        {renderedCells}
                      </SortableContext>
                    </DndContext>
                  )
                }
                return renderedCells
              })()}
            </TableRow>
          </TableHeader>

          {/* 表格内容 */}
          <TableBody className={cn("bg-background divide-y divide-border", bodyClassName)}>
            {data.map((item, index) => {
              const key = keyExtractor(item)
              const dynamicRowClassName = typeof rowClassName === 'function' ? rowClassName(item, index) : rowClassName
              return (
                <TableRow
                  key={key}
                  className={cn("group hover:bg-accent transition-colors", dynamicRowClassName, onRowClick && "cursor-pointer")}
                  onMouseDownCapture={onRowClick ? handleRowMouseDownCapture : undefined}
                  onClick={onRowClick ? (e) => handleRowClick(item, e) : undefined}
                >
                  {selectable && (() => {
                    const disabled = isRowSelectable ? !isRowSelectable(item) : false
                    return (
                      <TableCell
                        // 行点击的判据锚点 —— 见 handleRowClick 的注释。
                        data-selection-cell=""
                        className={cn("w-12 whitespace-nowrap text-left px-4 py-4", disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer")}
                        style={{ width: '48px' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (disabled) return
                          if (e.shiftKey) {
                            e.preventDefault()
                            window.getSelection()?.removeAllRanges()
                          }
                          const checked = !isSelected(item)
                          handleSelectionChange(item, checked, e.shiftKey)
                        }}
                      >
                        <Checkbox
                          checked={isSelected(item)}
                          disabled={disabled}
                          className="pointer-events-none"
                          aria-label={t('ui.table.selectItem', { key })}
                        />
                      </TableCell>
                    )
                  })()}
                  {columns.map((column) => (
                    <TableCell
                      key={`${key}-${column.key}`}
                      className={cn(column.width, "whitespace-nowrap text-left px-3 py-2 text-[13px] text-muted-foreground overflow-hidden",
                        ALIGN_CLASS[column.align ?? 'left'],
                        column.stickyRight && STICKY_RIGHT_CELL,
                        column.stickyLeft && STICKY_LEFT_CELL)}
                      style={column.width ? { width: column.width, maxWidth: column.width } : {}}
                    >
                      {column.render(item)}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
            {/* 加载更多指示器 */}
            {loadingMore && (
              <TableRow>
                <TableCell colSpan={columns.length + (selectable ? 1 : 0)} className="text-center py-4">
                  <div className="flex items-center justify-center space-x-2">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <span className="text-sm text-muted-foreground">{t('ui.table.loadingMore')}</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          </table>
        </div>
      </div>

      {/* 底部内容 */}
      {bottomContent && (
        <div className="flex-shrink-0 bg-muted/50 border-t border-border rounded-b-lg">
          {bottomContent}
        </div>
      )}
    </div>
  )
}